import { Fragment, useCallback, useEffect, useLayoutEffect, useState } from "react";
import {
  PLAN_ARTIFACT_KINDS,
  PROTOTYPE_KIND_LABEL,
  type BuilderPlan,
  type PlanArtifactKind,
  type PlanArtifactState,
  type PlanPreviewRef,
  type Prototype,
  type PrototypeDesignReference,
  type PrototypePreviewParts,
} from "@yagni/shared";
import { captureUiError } from "../sentry.js";
import { Button, Pane, Textarea } from "../ui/index.js";
import { PrototypeArtifact } from "../components/PrototypeArtifact.js";
import { PlanDesignReview } from "./PlanDesignReview.js";
import { DESIGN_COPY } from "./designCopy.js";
import { OpenInNewTab } from "./OpenInNewTab.js";

/** How often the section reads while a preview is on its way, and otherwise. */
const ACTIVE_POLL_MS = 5_000;
const QUIET_POLL_MS = 30_000;

export function useWorkItemPrototypes(workItemId: string, refreshKey = 0): Prototype[] {
  const [prototypes, setPrototypes] = useState<Prototype[]>([]);
  useEffect(() => {
    let cancelled = false;
    setPrototypes([]);
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      void fetch(`/api/prototypes?workItemId=${encodeURIComponent(workItemId)}`, {
        credentials: "include",
      }).then(async res => {
        if (!res.ok) return;
        const body = await res.json() as { prototypes?: Prototype[] } | Prototype[];
        if (!cancelled) setPrototypes(Array.isArray(body) ? body : body.prototypes ?? []);
      }).catch(() => { /* The next visible poll retries; keep the last cards. */ });
    };
    refresh();
    const timer = setInterval(refresh, ACTIVE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [workItemId, refreshKey]);
  return prototypes;
}

export interface PrototypeDesignSnapshot {
  planId: string;
  hash: string | null;
}

interface PlanDesign {
  snapshot: PrototypeDesignSnapshot | null;
  planId: string | null;
  planVersion: number;
  /** When the plan was written: a plan the live planning run wrote is not one it is revising. */
  createdAt: string | null;
  approved: boolean;
  draft: boolean;
  references: PrototypeDesignReference[];
  /** Reeve's artifacts of this plan, one per kind (the UI preview is `design`). */
  artifacts: PlanArtifactState[];
}

/** A reference stored before kinds existed reads as the plan's design. */
const isDesign = (ref: PrototypeDesignReference) => (ref.kind ?? "design") === "design";

/** The plan's artifacts; a backend from before kinds sends the design's status alone. */
function artifactsOf(plan: BuilderPlan | undefined): PlanArtifactState[] {
  if (!plan) return [];
  if (plan.artifacts) return plan.artifacts;
  return plan.previewStatus
    ? [{ kind: "design", status: plan.previewStatus, ref: plan.previewRef ?? null, attempts: 0, updatedAt: plan.createdAt }]
    : [];
}

function usePlanDesign(
  workItemId: string | undefined,
  gatePlanId: string | null,
  planState: "planning" | "implementation",
  active: boolean,
) {
  const [design, setDesign] = useState<PlanDesign | null>(null);
  const [tick, setTick] = useState(0);
  const pollMs = active || design?.artifacts.some(a => a.status === "drafting") ? ACTIVE_POLL_MS : QUIET_POLL_MS;
  useEffect(() => {
    if (!workItemId) {
      setDesign(null);
      return;
    }
    let cancelled = false;
    // The first read always runs: a case file opened in a background tab still
    // needs the plan's design hash, or its Approve is refused as "design changed".
    // Only the poll rests while the tab is hidden, and showing the tab reads at once.
    const refresh = async (force = false) => {
      if (!force && document.visibilityState === "hidden") return;
      try {
        const response = await fetch(`/api/work-items/${encodeURIComponent(workItemId)}/plans`, { credentials: "include" });
        if (!response.ok) return;
        const body: unknown = await response.json();
        if (!Array.isArray(body)) return;
        const plans = (body as BuilderPlan[]).sort((a, b) => b.version - a.version);
        const plan = gatePlanId ? plans.find(row => row.id === gatePlanId)
          : planState === "implementation" ? plans.find(row => row.status === "confirmed")
          : plans.find(row => row.status === "draft") ?? plans.find(row => row.status === "confirmed");
        const references = (plan?.prototypeReferences ?? []).filter(isDesign);
        if (!cancelled) setDesign({
          snapshot: plan ? { planId: plan.id, hash: references.length > 0 ? plan.prototypeReferencesHash ?? null : null } : null,
          planId: plan?.id ?? null,
          planVersion: plan?.version ?? 0,
          createdAt: plan?.createdAt ?? null,
          approved: plan?.status === "confirmed",
          draft: plan?.status === "draft",
          references,
          artifacts: artifactsOf(plan),
        });
      } catch { /* Retry the read on the next visible poll. */ }
    };
    void refresh(true);
    const timer = setInterval(() => { void refresh(); }, pollMs);
    const onVisible = () => { void refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { cancelled = true; clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [workItemId, gatePlanId, planState, pollMs, tick]);
  const reload = useCallback(() => setTick(value => value + 1), []);
  return { design, reload };
}

interface PrototypesStripProps {
  prototypes: Prototype[];
  workItemId?: string;
  title?: string;
  onCreated?: () => void;
  gatePlanId?: string | null;
  planState?: "planning" | "implementation";
  allowCreate?: boolean;
  onDesignSnapshot?: (snapshot: PrototypeDesignSnapshot | null) => void;
  /** Reeve is writing (or revising) the plan right now. */
  planning?: boolean;
  /** When the live planning run started, so a plan it already wrote is not read as one it revises. */
  planningSince?: string | null;
  /** The work item is finished: the approved design reads as the record, with no notice. */
  done?: boolean;
}

function viewsFact(parts: PrototypePreviewParts) {
  if (parts.views.includes("today") && parts.views.includes("proposed")) {
    return <><b>{DESIGN_COPY.today}</b> and <b>{DESIGN_COPY.proposed}</b></>;
  }
  return <b>{DESIGN_COPY.viewProposed}</b>;
}

/** Whether a revision's parts have anything to list: a page shown as it is has none. */
function hasDesignFacts(parts: PrototypePreviewParts | null | undefined): parts is PrototypePreviewParts {
  return Boolean(parts && (parts.views.length || parts.states.length || parts.choices.length || parts.sourceFiles.length));
}

function DesignFacts({ parts }: { parts: PrototypePreviewParts | null | undefined }) {
  if (!parts) return null;
  const files = parts.sourceFiles;
  const first = files[0]?.split("/").pop();
  return (
    <ul className="cf-design-facts">
      {parts.views.length > 0 && <li><span className="k">{DESIGN_COPY.views}</span><span>{viewsFact(parts)}</span></li>}
      {parts.states.length > 0 && <li><span className="k">{DESIGN_COPY.states}</span><span>{parts.states.map(state => state.label).join(" · ")}</span></li>}
      {parts.choices.map((choice, index) => (
        <li key={choice.key}>
          <span className="k">{DESIGN_COPY.choice}</span>
          <span>{DESIGN_COPY.choiceOpen(choice.options.map(option => option.label), index + 1)}</span>
        </li>
      ))}
      {first && (
        <li>
          <span className="k">{DESIGN_COPY.readFrom}</span>
          <span><code>{first}</code>{DESIGN_COPY.readFromMore(files.length - 1, parts.baseSha)}</span>
        </li>
      )}
    </ul>
  );
}

function byline(ref: Pick<PrototypeDesignReference, "revision"> & Partial<Pick<PrototypeDesignReference, "planId">>): string | undefined {
  if (ref.planId === undefined) return undefined;
  if (ref.planId === null) return DESIGN_COPY.bylinePerson;
  return ref.revision > 1 ? DESIGN_COPY.bylineFeedback : DESIGN_COPY.bylineFirst;
}

function DraftingPane({ text, prior }: { text: string; prior?: number }) {
  return (
    <Pane material="dashed" className="cf-card cf-design-drafting" data-testid="design-drafting">
      <span className="cf-design-pulse" aria-hidden="true" />
      <span className="cf-design-drafting-text">{text}</span>
      {prior !== undefined && <span className="cf-design-rev">R{prior}</span>}
    </Pane>
  );
}

/** Every attempt failed: say so, and offer another draft while the plan is open. */
function FailedPane({ workItemId, planId, kind, canRetry, onRetried }: { workItemId?: string; planId: string | null; kind: PlanArtifactKind; canRetry: boolean; onRetried: () => void }) {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const retry = async () => {
    if (!workItemId || !planId) return;
    setRetrying(true); setRetryError(null);
    try {
      const res = await fetch(`/api/work-items/${encodeURIComponent(workItemId)}/plans/${encodeURIComponent(planId)}/artifacts/${encodeURIComponent(kind)}/retry`, { method: "POST", credentials: "include" });
      if (!res.ok) { setRetryError(DESIGN_COPY.retryFailed); return; }
      onRetried();
    } catch {
      captureUiError(new Error("Plan preview retry failed"), { area: "prototypes", operation: "preview_retry", severity: "error", expected: false });
      setRetryError(DESIGN_COPY.retryFailed);
    } finally { setRetrying(false); }
  };
  return (
    <Pane material="dashed" className="cf-card cf-design-drafting" data-testid="design-failed">
      <span className="cf-design-drafting-text">{DESIGN_COPY.failed}</span>
      {canRetry && (
        <Button size="sm" variant="secondary" disabled={retrying} onClick={() => void retry()}>
          {retrying ? DESIGN_COPY.draftingAgain : DESIGN_COPY.draftAgain}
        </Button>
      )}
      {retryError && <p role="alert" className="cf-design-error">{retryError}</p>}
    </Pane>
  );
}

type DesignRef = Pick<PrototypeDesignReference, "prototypeId" | "revision" | "title"> &
  Partial<Pick<PrototypeDesignReference, "kind" | "planId" | "parts">>;

/** One revision the plan carries (or carried too late), with what it shows. */
function DesignCard({ designRef, settled, open, onToggle, onClose, kept, notice, warn }: {
  designRef: DesignRef;
  /** Approved, or arrived after approval: printed, and the review reads only. */
  settled: boolean;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  kept: boolean;
  notice: string | null;
  warn: boolean;
}) {
  const missing = designRef.parts?.missing ?? [];
  const facts = hasDesignFacts(designRef.parts);
  return (
    <Pane material={settled ? "printed" : "staged"} className={facts ? "cf-card cf-design-pane" : "cf-card cf-design-pane is-bare"}>
      <PrototypeArtifact
        data={{ prototypeId: designRef.prototypeId, title: designRef.title, currentRevision: designRef.revision, url: `/prototypes/${encodeURIComponent(designRef.prototypeId)}?rev=${designRef.revision}` }}
        onOpen={onToggle}
        kindLabel={PROTOTYPE_KIND_LABEL[designRef.kind ?? "design"]}
        byline={byline(designRef)}
        showFoot={false}
      />
      <div className="cf-design-side">
        {facts && (
          <>
            <div className="cf-design-sub">{DESIGN_COPY.whatItShows}</div>
            <DesignFacts parts={designRef.parts} />
          </>
        )}
        <div className="cf-design-acts">
          <Button size="sm" variant="secondary" onClick={onToggle}>{DESIGN_COPY.reviewHere}</Button>
          <OpenInNewTab prototypeId={designRef.prototypeId} revision={designRef.revision} />
        </div>
      </div>
      {missing.length > 0 && <p className="cf-design-notice is-honest">{DESIGN_COPY.missing(missing)}</p>}
      {kept && <p className="cf-design-notice">{DESIGN_COPY.kept(designRef.revision)}</p>}
      {notice && <p className={warn ? "cf-design-notice is-honest" : "cf-design-notice"}>{notice}</p>}
      {open && <PlanDesignReview prototypeId={designRef.prototypeId} title={designRef.title} revision={designRef.revision} approved={settled} onClose={onClose} parts={designRef.parts ?? null} />}
    </Pane>
  );
}

/** A person's own prototype for the work item, drafted from what they describe. */
function CreatePrototype({ workItemId, title, onCreated, showEmpty }: { workItemId: string; title: string; onCreated?: () => void; showEmpty: boolean }) {
  const [creating, setCreating] = useState(false);
  const [spec, setSpec] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generate = async () => {
    if (!spec.trim()) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch('/api/prototypes/draft', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title, spec: spec.trim(), workItemId, automatic: false }) });
      if (!res.ok) throw new Error();
      const result = await res.json() as { status: string; reason?: string };
      if (result.status !== 'created' && result.status !== 'reused') { setError(result.reason ?? 'Could not draft a prototype. Try again or continue without one.'); return; }
      setCreating(false); setSpec(""); onCreated?.();
    } catch { captureUiError(new Error('Prototype generation failed'), { area: 'prototypes', operation: 'draft', severity: 'error', expected: false }); setError('Could not draft a prototype. Try again or continue without one.'); }
    finally { setBusy(false); }
  };
  if (!creating) return (
    <div className="cf-design-acts">
      {showEmpty && <span className="cf-design-quiet">{DESIGN_COPY.emptyCreate}</span>}
      <Button size="sm" variant="secondary" onClick={() => setCreating(true)}>Create prototype</Button>
    </div>
  );
  return (
    <Pane material="dashed" className="cf-card cf-design-draft">
      <form onSubmit={event => { event.preventDefault(); void generate(); }}>
        <label className="cf-design-field">
          <span>What should the prototype explore?</span>
          <Textarea value={spec} maxLength={10000} rows={3} autosize onChange={event => setSpec(event.target.value)} placeholder="The screen, the flow, or the state you are unsure about" />
        </label>
        <div className="cf-design-acts">
          <Button type="submit" size="sm" disabled={busy || !spec.trim()}>{busy ? 'Drafting…' : 'Generate prototype'}</Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => { setCreating(false); setError(null); }} disabled={busy}>Cancel</Button>
        </div>
        {error && <p role="alert" className="cf-design-error">{error}</p>}
      </form>
    </Pane>
  );
}

/** The revision a non-design kind drafted for the plan, once it landed. */
function draftedRefs(artifact: PlanArtifactState | null): DesignRef[] {
  if (!artifact?.ref) return [];
  if (artifact.status !== "ready" && artifact.status !== "incomplete") return [];
  return [{ ...artifact.ref, title: PROTOTYPE_KIND_LABEL[artifact.kind], kind: artifact.kind }];
}

/**
 * One artifact kind's part of the Design section: the revisions the plan
 * carries (the plan's references for the design kind, the drafted revision
 * for any other), the one that arrived too late, and whether a draft is on
 * its way or failed.
 */
function kindState(design: PlanDesign | null, kind: PlanArtifactKind) {
  const artifact = design?.artifacts.find(a => a.kind === kind) ?? null;
  const status = artifact?.status ?? null;
  const references = kind === "design" ? design?.references ?? [] : draftedRefs(artifact);
  const lateRef: PlanPreviewRef | null = status === "late" ? artifact?.ref ?? null : null;
  return {
    kind,
    references,
    lateRef,
    drafting: status === "drafting" && references.length === 0,
    failed: status === "failed" && references.length === 0,
  };
}

/**
 * Everything the Design section shows, derived once from the plan's design and
 * the work item's prototypes, so the render reads one state, not a web of
 * booleans.
 */
function designState(design: PlanDesign | null, prototypes: Prototype[], planning: boolean, planningSince: string | null) {
  const kinds = PLAN_ARTIFACT_KINDS.map(kind => kindState(design, kind));
  const references = kinds.flatMap(k => k.references);
  const lateRefs = kinds.flatMap(k => (k.lateRef ? [k.lateRef] : []));
  const shown = (p: Prototype) =>
    references.some(ref => ref.prototypeId === p.id && ref.revision === p.currentRevision)
    || lateRefs.some(ref => ref.prototypeId === p.id && ref.revision === p.currentRevision);
  const settled = !!design?.approved || lateRefs.length > 0;
  return {
    kinds,
    references,
    settled,
    // Later revisions and a person's own prototypes, newest first.
    experiments: prototypes.filter(p => !shown(p)).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    ),
    // The work item's preview already exists: the next draft is its next revision.
    designPrototype: prototypes.find(p => references.some(ref => ref.prototypeId === p.id))
      ?? prototypes.find(p => p.creditedWorker === "reeve"),
    // Reeve is revising an earlier plan's design; a plan the live run already
    // wrote waits on its own preview before its gate, and shows it.
    revisingWithDraft: planning && references.some(ref => ref.planId !== null)
      && !(planningSince && design?.createdAt && Date.parse(design.createdAt) >= Date.parse(planningSince)),
    hasDesign: kinds.some(k => k.references.length > 0 || k.drafting || k.failed || k.lateRef !== null),
    designLabel: settled ? DESIGN_COPY.approved : DESIGN_COPY.inPlan,
    /** A person's revision Reeve carried onto a re-plan instead of redrawing. */
    kept: (ref: DesignRef) => ref.planId === null && (design?.planVersion ?? 0) > 1 && !design?.approved,
  };
}

/**
 * The case file's Design section: the UI preview the plan carries, from the
 * moment Reeve starts drafting it to the finished item. Glass while it waits
 * on the plan's approval, print once approved; dashed while a draft is on
 * its way. A preview is a visual reference for the plan, never a second
 * plan, so it reads under the plan rather than as a surface of its own.
 */
export function PrototypesStrip({ prototypes, workItemId, title = "Prototype", onCreated, gatePlanId = null, planState = "planning", allowCreate = true, onDesignSnapshot, planning = false, planningSince = null, done = false }: PrototypesStripProps) {
  const { design, reload } = usePlanDesign(workItemId, gatePlanId, planState, planning);
  useLayoutEffect(() => { onDesignSnapshot?.(design?.snapshot ?? null); }, [design, onDesignSnapshot]);
  // The plan's design opens for review here, above the gate, not on another page.
  const [reviewing, setReviewing] = useState<string | null>(null);
  const canCreate = !!workItemId && allowCreate;
  const view = designState(design, prototypes, planning, planningSince);
  if (!view.hasDesign && view.experiments.length === 0 && !canCreate) return null;

  const card = (designRef: DesignRef, notice: string | null, warn: boolean) => (
    <DesignCard
      key={`${designRef.prototypeId}:${designRef.revision}`}
      designRef={designRef}
      settled={view.settled}
      open={reviewing === designRef.prototypeId}
      onToggle={() => setReviewing(open => (open === designRef.prototypeId ? null : designRef.prototypeId))}
      onClose={() => setReviewing(null)}
      kept={view.kept(designRef)}
      notice={notice}
      warn={warn}
    />
  );
  const { designPrototype } = view;

  return (
    <section className="cf-section cf-design" data-testid="case-file-prototypes">
      <div className="cf-sect">{DESIGN_COPY.section}</div>
      {view.hasDesign && <section aria-label={view.designLabel} className="cf-design-group">
        <div className="cf-design-sub">{view.designLabel}</div>
        {view.revisingWithDraft && <DraftingPane text={DESIGN_COPY.revising} />}
        {view.kinds.map(({ kind, references, lateRef, drafting, failed }) => (
          <Fragment key={kind}>
            {drafting && (kind === "design" && designPrototype
              ? <DraftingPane text={DESIGN_COPY.redrafting(designPrototype.currentRevision + 1)} prior={designPrototype.selectedRevision ?? designPrototype.currentRevision} />
              : <DraftingPane text={DESIGN_COPY.drafting} />)}
            {failed && <FailedPane workItemId={workItemId} planId={design?.planId ?? null} kind={kind} canRetry={!!design?.draft} onRetried={reload} />}
            {references.map(ref => card(ref, design?.approved && !done ? DESIGN_COPY.approvedNotice : null, false))}
            {lateRef && card({ prototypeId: lateRef.prototypeId, revision: lateRef.revision, title: prototypes.find(p => p.id === lateRef.prototypeId)?.title ?? title, kind }, DESIGN_COPY.late, true)}
          </Fragment>
        ))}
      </section>}
      {view.experiments.length > 0 && <section aria-label={DESIGN_COPY.experiments} className="cf-design-group">
        <div className="cf-design-sub">{DESIGN_COPY.experiments}</div>
        {design?.approved && !done && <p className="cf-design-quiet">Later versions do not change the approved design. Adopt them through a new plan review.</p>}
        <div className="cf-design-grid">
          {view.experiments.map((p) => (
            <PrototypeArtifact
              key={p.id}
              data={{
                prototypeId: p.id,
                title: p.title,
                currentRevision: p.currentRevision,
                url: p.url,
                ...(p.workItemId ? { workItemId: p.workItemId } : {}),
                ...(p.creditedWorker ? { creditedWorker: p.creditedWorker } : {}),
              }}
            />
          ))}
        </div>
      </section>}
      {canCreate && workItemId && <CreatePrototype workItemId={workItemId} title={title} onCreated={onCreated} showEmpty={!view.hasDesign && view.experiments.length === 0} />}
    </section>
  );
}
